import { ExclusionNote } from "@src/common/ExclusionNote/ExclusionNote";
import { IllustratedRouteLayout, type ResponsiveDevice } from "@src/common/IllustratedRouteLayout";
import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber";
import { BodyText, HeadingDiv } from "@src/common/styled-components";
import { EAP_PHONE_NUMBER } from "@src/constants/contact-constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import { Fragment, useEffect } from "react";

export const Contact = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const { isTimerRunning, stopTimer } = useTimerContext();
  const { clearDataExcluding, clearCompletedRoutes, clearAdditionalSavedModalData, setData } =
    useApplicationContext();
  const location = useLocation();
  const navigationState = location.state ?? null;
  const exclusionNote: string | null = navigationState?.exclusionNote ?? null;
  const isEap: boolean = navigationState?.isEap ?? false;

  useEffect(() => {
    setData("isReturningFromContactFlow", true); // Set flag to indicate user has been on Contact Us page
    clearDataExcluding(["userUuid", "isReturningFromContactFlow"]); // Clear all data except userUuid and isReturningFromContactFlow to keep user identification intact for logging while clearing any other potentially stale data in context when navigating to Contact Us page
    clearCompletedRoutes(); // Clear completed routes to reset navigation flow for users who navigate to Contact Us page from different entry points in the app
    clearAdditionalSavedModalData(); // Clear any additional saved modal data to ensure a fresh state for the Contact Us page
  }, []);

  useEffect(() => {
    // Stop the timer when this component mounts
    if (isTimerRunning) {
      stopTimer();
    }
  }, [isTimerRunning, stopTimer]);

  // Helper functions
  const renderMessage = () => {
    if (isEap) {
      return (
        <BodyText device={device} mobileBrandColor>
          <Fragment>
            If you are using an EAP, please call us directly at{" "}
            <span style={{ fontWeight: "700" }}>{EAP_PHONE_NUMBER}</span> to schedule your
            appointment instead of using our online self-scheduling system. Thank you for your
            cooperation!
          </Fragment>
        </BodyText>
      );
    }

    if (exclusionNote) {
      return <ExclusionNote exclusionInd={true} exclusionNote={exclusionNote} />;
    }

    return (
      <BodyText device={device} mobileBrandColor>
        Some situations require a quick call to complete scheduling. Please call to finish setting
        up your appointment call{" "}
        <span style={{ fontWeight: "700" }}>
          <InvocaPhoneNumber />
        </span>
        .
      </BodyText>
    );
  };

  let device: ResponsiveDevice = "desktop";
  if (isMobile) {
    device = "mobile";
  } else if (isTablet) {
    device = "tablet";
  }

  return (
    <IllustratedRouteLayout metaTitle="Contact Us" device={device}>
      <HeadingDiv device={device}>Contact us to complete your scheduling</HeadingDiv>
      {renderMessage()}
    </IllustratedRouteLayout>
  );
};
